import React from 'react';
import {useRef} from 'react';

import {motion} from 'framer-motion';
import {useInView} from 'framer-motion';
import {useIntl} from 'react-intl';

export const ThreeElements: React.FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useIntl();
  const ref = useRef(null);
  const isInView = useInView(ref, {once: true, amount: 0.3});

  const elements = [
    {
      title: intl.formatMessage({id: 'home.threeElements.pacingTitle'}),
      description: intl.formatMessage({
        id: 'home.threeElements.pacingDescription',
      }),
      image: '/images/home/threeways1.png',
      color: 'blue',
      features: [
        intl.formatMessage({id: 'home.threeElements.pacingFeature1'}),
        intl.formatMessage({id: 'home.threeElements.pacingFeature2'}),
        intl.formatMessage({id: 'home.threeElements.pacingFeature3'}),
      ],
    },
    {
      title: intl.formatMessage({id: 'home.threeElements.trainingTitle'}),
      description: intl.formatMessage({
        id: 'home.threeElements.trainingDescription',
      }),
      image: '/images/home/threeways2.png',
      color: 'green',
      features: [
        intl.formatMessage({id: 'home.threeElements.trainingFeature1'}),
        intl.formatMessage({id: 'home.threeElements.trainingFeature2'}),
        intl.formatMessage({id: 'home.threeElements.trainingFeature3'}),
      ],
    },
    {
      title: intl.formatMessage({id: 'home.threeElements.racingTitle'}),
      description: intl.formatMessage({
        id: 'home.threeElements.racingDescription',
      }),
      image: '/images/home/threeways3.png',
      color: 'purple',
      features: [
        intl.formatMessage({id: 'home.threeElements.racingFeature1'}),
        intl.formatMessage({id: 'home.threeElements.racingFeature2'}),
        intl.formatMessage({id: 'home.threeElements.racingFeature3'}),
      ],
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return {
          bg: 'bg-blue-50',
          accent: 'bg-blue-600',
          text: 'text-blue-600',
          border: 'border-blue-200',
        };
      case 'green':
        return {
          bg: 'bg-green-50',
          accent: 'bg-green-600',
          text: 'text-green-600',
          border: 'border-green-200',
        };
      case 'purple':
        return {
          bg: 'bg-purple-50',
          accent: 'bg-purple-600',
          text: 'text-purple-600',
          border: 'border-purple-200',
        };
      default:
        return {
          bg: 'bg-gray-50',
          accent: 'bg-gray-600',
          text: 'text-gray-600',
          border: 'border-gray-200',
        };
    }
  };

  return (
    <section ref={ref} className="py-4 lg:py-8 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-20 lg:mb-24"
          // initial={{opacity: 0, y: 50}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, ease: 'easeOut'}}
        >
          <h2 className="text-4xl md:text-6xl font-light text-black mb-4">
            <strong className="font-bold">
              {intl.formatMessage({id: 'home.threeElements.mainTitle'})}
            </strong>
            .<br />
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {intl.formatMessage({id: 'home.threeElements.mainDescription'})}
          </p>
        </motion.div>

        {/* Three Elements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {elements.map((element, index) => {
            const colors = getColorClasses(element.color);

            return (
              <motion.div
                key={index}
                className="relative group"
                // initial={{opacity: 0, y: 30}}
                // animate={isInView ? {opacity: 1, y: 0} : {}}
                // transition={{duration: 0.8, delay: 0.2 + index * 0.2}}
              >
                {/* Card Container */}
                <div
                  className={`${colors.bg} rounded-3xl h-full border ${colors.border} hover:shadow-lg transition-all duration-300 group-hover:scale-105`}
                >
                  {/* Image */}
                  <motion.div
                    className="relative overflow-hidden"
                    // initial={{scale: 0.9}}
                    // animate={isInView ? {scale: 1} : {}}
                    // transition={{duration: 0.6, delay: 0.4 + index * 0.2}}
                  >
                    <img
                      src={element.image}
                      alt={element.title}
                      className="w-full object-cover group-hover:scale-110 transition-transform duration-500 rounded-t-3xl"
                    />

                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

                    {/* Element number */}
                    <div className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-gray-800">
                        {index + 1}
                      </span>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div className="space-y-6 p-4 lg:p-6">
                    {/* Title */}
                    <motion.h3
                      className={`text-base font-bold ${colors.text} mb-3`}
                      // initial={{opacity: 0, x: -20}}
                      // animate={isInView ? {opacity: 1, x: 0} : {}}
                      // transition={{duration: 0.6, delay: 0.6 + index * 0.2}}
                    >
                      {element.title}
                    </motion.h3>

                    {/* Description */}
                    <motion.p
                      className="text-gray-600 leading-relaxed text-base"
                      // initial={{opacity: 0, x: -20}}
                      // animate={isInView ? {opacity: 1, x: 0} : {}}
                      // transition={{duration: 0.6, delay: 0.8 + index * 0.2}}
                    >
                      {element.description}
                    </motion.p>

                    {/* Features List */}
                    <motion.div
                      className="space-y-3"
                      // initial={{opacity: 0, y: 20}}
                      // animate={isInView ? {opacity: 1, y: 0} : {}}
                      // transition={{duration: 0.6, delay: 1.0 + index * 0.2}}
                    >
                      {element.features.map((feature, featureIndex) => (
                        <motion.div
                          key={featureIndex}
                          className="flex items-center gap-3"
                          // initial={{opacity: 0, x: -10}}
                          // animate={isInView ? {opacity: 1, x: 0} : {}}
                          // transition={{
                          //   duration: 0.4,
                          //   delay: 1.2 + index * 0.2 + featureIndex * 0.1,
                          // }}
                        >
                          <div
                            className={`w-2 h-2 ${colors.accent} rounded-full`}
                          ></div>
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Accent line */}
                  {/* <motion.div
                    className={`h-1 ${colors.accent} rounded-full mt-6`}
                    initial={{width: 0}}
                    animate={isInView ? {width: '100%'} : {}}
                    transition={{duration: 1, delay: 1.4 + index * 0.2}}
                  /> */}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-20 lg:mt-24 text-center"
          initial={{opacity: 0, y: 30}}
          animate={isInView ? {opacity: 1, y: 0} : {}}
          transition={{duration: 0.8, delay: 1.5}}
        >
          <motion.p
            className="text-xl lg:text-2xl text-gray-600 mb-10 lg:mb-12 max-w-4xl mx-auto leading-relaxed"
            initial={{opacity: 0}}
            animate={isInView ? {opacity: 1} : {}}
            transition={{duration: 0.6, delay: 1.7}}
          >
            {intl.formatMessage({id: 'home.threeElements.bottomDescription'})}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center"
            initial={{opacity: 0, scale: 0.8}}
            animate={isInView ? {opacity: 1, scale: 1} : {}}
            transition={{duration: 0.6, delay: 1.9}}
          >
            <motion.a
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-neutral-900 text-white font-medium rounded-full text-lg transition-all duration-300 hover:bg-neutral-800 hover:scale-105 hover:shadow-xl"
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              href="/store"
            >
              {intl.formatMessage({id: 'home.threeElements.shopButton'})}
            </motion.a>
            <motion.a
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-neutral-900 text-white font-medium rounded-full text-lg transition-all duration-300 hover:bg-neutral-800 hover:scale-105 hover:shadow-xl"
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              href="https://blog.stryd.com"
            >
              {intl.formatMessage({id: 'home.threeElements.learnMoreButton'})}
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
